// Live's library card menu (#590, spec docs/specs/web/S1-library-screens.md decision 22): the
// "..." of a library file, live's items in live's order (`useAssetActionsMenu.tsx`,
// `useAssetMenuShared.tsx`, read for behaviour), each shown when live shows it and our API can
// do it:
//   Download; Delete (who may delete the file, behind the library's question); Copy link; Move to
//   project (any member, live's picker, S1c); Move to AI Actor (an uploaded image, behind live's
//   question); Edit and Animate (an image, in the composer); Clone (an image, in live's Advanced
//   mode with it as its reference, issue 828 item 23); Layerize (an image, in live's Layerize
//   mode, where the team may use its row, G13); a video's Open in the editor (its own project,
//   in a new tab, as live's) and Edit (the Video Edit tool on it, S1c); Copy Prompt and Save
//   Prompt (a simple generation's picture, and a simple creative a brand import brought in, from
//   live's record, issue 828, bug 24; Copy Prompt also a text-to-video's video); Re-index
//   (spec S3, every file, while semantic search is on: indexed for search again, the card then
//   says it is indexing); the file's project.
// Live's Mark as Inspiration (no inspiration mark on a file), Upload as ad (the ads area), Remix
// and a video's Clone (multi-shot videos, D4) wait for what they need (S1c, #590). The trash
// keeps its Restore button and no menu (live has no trash). The preview's "..." holds the same
// items but Download and Delete, which its bar shows (live's). A Video Studio Library tab card
// (`place="studio"`, live's `useVideoCardActions`, captured 2026-10-26, spec open-from-studio)
// holds live's items alone, in its order: Re-index, Open in the editor, Edit and Copy Prompt;
// its Clone and Remix wait for multi-shot (G16).
import { ActionIcon, Loader, Menu, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Check,
  Copy,
  Download,
  Ellipsis,
  FilePlus,
  FolderInput,
  FolderSymlink,
  Layers3,
  Link as LinkIcon,
  Pencil,
  Search,
  SquarePlay,
  Trash2,
  Type,
  User,
  WandSparkles,
} from "lucide-react";
import { useEffect, useState } from "react";

import { downloadSignedFile } from "../../api/download";
import {
  getListActorsQueryKey,
  useGetCatalog,
  useGetImportedGeneration,
  useGetJob,
  useMoveLibraryImageToActor,
} from "../../api/generated/endpoints/generation/generation";
import { useReindexAsset } from "../../api/generated/endpoints/assets/assets";
import { useListFolders } from "../../api/generated/endpoints/teams/teams";
import type { Asset } from "../../api/generated/model";
import { refreshLibraryLists } from "../../api/library-lists";
import { isGenerationType } from "../jobs/type-names";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { ErrorAlert } from "../ErrorAlert";
import { useSemanticSearch } from "../features";
import { MoveToProject } from "./MoveToProject";
import { useProjectLink } from "./project-link";
import { PROMPT_SAVED, SIMPLE_TYPES, useSavePrompt } from "./save-prompt";
import { usePrefetchTrashDays } from "./trash-days";

/** How long "Link copied" and "Copied" show (live's 2 s). */
const COPIED_MS = 2000;

export type AssetMenuProps = {
  teamId: string;
  brandId: string;
  asset: Asset;
  /** In the preview's bar, which shows Download and Delete itself. */
  inPreview?: boolean | undefined;
  /** Where the trigger sits: the card's dark pill, or the preview's bar. */
  variant?: "card" | "preview" | undefined;
} & (
  | {
      /** The library's card and preview (and the Studio's preview): every item above. */
      place?: "library" | undefined;
      /** The member may change this file: delete it, or move it to an actor (D6). */
      mayChange: boolean;
      onDelete: (asset: Asset) => void;
    }
  | {
      /**
       * A Video Studio Library tab card (live's `useVideoCardActions`, captured 2026-10-26, spec
       * open-from-studio): live's items alone, in its order, Re-index, Open in the editor, Edit
       * and Copy Prompt. Nothing in it deletes or moves the file.
       */
      place: "studio";
    }
);

/** The link live's "Copy link" copies: ours opens the file in the library's preview. */
export function assetLink(teamId: string, brandId: string, assetId: string): string {
  return `${window.location.origin}/teams/${teamId}/brands/${brandId}/library?preview=${assetId}`;
}

/** A label that says "copied" for live's 2 s after a copy, or that the copy failed. */
function useCopied() {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  useEffect(() => {
    if (state === "idle") return;
    const timer = setTimeout(() => setState("idle"), COPIED_MS);
    return () => clearTimeout(timer);
  }, [state]);
  return {
    state,
    copy: (text: string) => {
      navigator.clipboard.writeText(text).then(
        () => setState("copied"),
        () => setState("failed"),
      );
    },
  };
}

export function AssetMenu(props: AssetMenuProps) {
  const { teamId, brandId, asset } = props;
  // A Studio card holds live's Video Studio items alone: nothing downloads, deletes or moves.
  const library = props.place !== "studio";
  const mayChange = props.place !== "studio" && props.mayChange;
  const onDelete = props.place === "studio" ? undefined : props.onDelete;
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);
  const prefetchDays = usePrefetchTrashDays(teamId, brandId);
  // Opened once: the job's read stays on from then, so another open asks nothing again.
  const [asked, setAsked] = useState(false);
  const [moving, setMoving] = useState(false);
  const [filing, setFiling] = useState(false);
  const link = useCopied();
  const prompt = useCopied();
  const saving = useSavePrompt(teamId, brandId);
  // A generation's saved file: its origin names a generation's job the team can read. An editor's
  // render is no generation job, and a deleted generation keeps its saved copies with no job, so
  // neither asks for one, nor offers the prompt's items.
  const origin = asset.origin !== null && isGenerationType(asset.origin.job_type) ? asset.origin : null;
  // Its job, read once the menu opens: its type and its prompt decide the prompt items, as live's
  // asset carries its generation's; a read that fails leaves them out and is not asked again.
  const jobRead = useGetJob(teamId, origin?.job_id ?? "", {
    query: { enabled: asked && origin !== null, retryOnMount: false, refetchOnWindowFocus: false },
  });
  const job = jobRead.data;
  // A creative a brand import brought in: a generation of live's with no job of ours (#844). Its
  // record (#864) says how live made it, read once the menu opens; a 404 is none, not asked again.
  const importedCreative = asset.kind === "image" && asset.source === "generation" && asset.origin === null;
  const importedRead = useGetImportedGeneration(teamId, brandId, asset.id, {
    query: { enabled: asked && importedCreative, retry: false, retryOnMount: false, refetchOnWindowFocus: false },
  });
  // Live's Copy Prompt and Save Prompt on a creative of a SIMPLE generation
  // (`isCopyCreativePrompt`); the template saved names the file as its source.
  const importedSimple = importedRead.data?.generation_type === "SIMPLE";
  const folders = useListFolders(teamId, brandId, { query: { enabled: opened && library && asset.folder_id !== null } }).data;
  const move = useMoveLibraryImageToActor({
    mutation: {
      onSuccess: async () => {
        setMoving(false);
        await Promise.all([
          refreshLibraryLists(queryClient, teamId, brandId),
          queryClient.invalidateQueries({ queryKey: getListActorsQueryKey(teamId, brandId) }),
        ]);
      },
    },
  });
  const [downloadFailed, setDownloadFailed] = useState(false);
  const semantic = useSemanticSearch();
  const reindex = useReindexAsset();
  const label = `More actions for ${asset.name}`;
  const look =
    props.variant === "preview"
      ? ({ variant: "subtle", color: "gray", c: "var(--cai-color-viewer-text)" } as const)
      : ({ variant: "text-white" } as const);
  const trigger = (
    <ActionIcon {...look} aria-label={label}>
      <Ellipsis size={20} aria-hidden />
    </ActionIcon>
  );
  // Live's image actions (`isImageActionsAsset`) are on all its image files but a product entry,
  // which ours lists on Products, not in the library: so on every image here.
  const image = asset.kind === "image";
  // Live's Layerize is behind its flag (`capabilities.imageLayerize`); ours, where the team may use
  // a Layerize row, as the Studio's Layerize tool (G13). The catalog is read once the menu opens.
  const catalog = useGetCatalog({ query: { enabled: asked && image } }).data;
  const layerize = image && catalog !== undefined && catalog.image_models.some((model) => model.job_types.includes("image.layerize"));
  const simple = job !== undefined && SIMPLE_TYPES.includes(job.type) && image;
  const textVideo = job !== undefined && job.type === "video.text" && asset.kind === "video";
  const promptText = (job?.params?.prompt ?? importedRead.data?.prompt ?? "").trim();
  const folder = asset.folder_id === null ? undefined : folders?.find((each) => each.id === asset.folder_id);
  // The Library page's project, unless the page drawing the Library opens it itself (Pro's).
  const projectLink = useProjectLink();
  const studio = (search: Record<string, unknown>) => ({
    to: "/teams/$teamId/brands/$brandId/generate" as const,
    params: { teamId, brandId },
    search,
  });
  return (
    <>
      <Menu position="right-end" withinPortal opened={opened} onChange={(next) => {
          setOpened(next);
          if (next) setAsked(true);
          // Its Delete's question names the trash's days: read them as the menu opens.
          if (next && mayChange && props.inPreview !== true) prefetchDays();
        }}>
        <Menu.Target>{trigger}</Menu.Target>
        <Menu.Dropdown aria-label={label}>
          {props.inPreview === true || !library || asset.urls.original === null ? null : (
            <Menu.Item
              leftSection={<Download size={14} aria-hidden />}
              onClick={() => {
                setDownloadFailed(false);
                downloadSignedFile(asset.urls.original!, asset.name).catch(() => setDownloadFailed(true));
              }}
            >
              Download
            </Menu.Item>
          )}
          {props.inPreview === true || !mayChange || onDelete === undefined ? null : (
            <Menu.Item leftSection={<Trash2 size={14} aria-hidden />} onClick={() => onDelete(asset)}>
              Delete
            </Menu.Item>
          )}
          {library ? (
            <>
              <Menu.Item
                closeMenuOnClick={false}
                leftSection={link.state === "copied" ? <Check size={14} aria-hidden /> : <LinkIcon size={14} aria-hidden />}
                onClick={() => link.copy(assetLink(teamId, brandId, asset.id))}
              >
                {link.state === "copied" ? "Link copied" : link.state === "failed" ? "Failed to copy" : "Copy link"}
              </Menu.Item>
              <Menu.Item leftSection={<FolderInput size={14} aria-hidden />} onClick={() => setFiling(true)}>
                Move to project
              </Menu.Item>
            </>
          ) : null}
          {image && asset.source === "upload" && mayChange ? (
            <Menu.Item leftSection={<User size={14} aria-hidden />} onClick={() => setMoving(true)}>
              Move to AI Actor
            </Menu.Item>
          ) : null}
          {semantic ? (
            <Menu.Item
              leftSection={reindex.isPending ? <Loader size={14} /> : <Search size={14} aria-hidden />}
              closeMenuOnClick={false}
              disabled={reindex.isPending}
              onClick={() =>
                reindex.mutate(
                  { teamId, brandId, assetId: asset.id },
                  {
                    onSuccess: async () => {
                      setOpened(false);
                      await refreshLibraryLists(queryClient, teamId, brandId);
                    },
                  },
                )
              }
            >
              Re-index
            </Menu.Item>
          ) : null}
          {image ? (
            <>
              <Menu.Item leftSection={<Pencil size={14} aria-hidden />} renderRoot={(root) => <Link {...root} {...studio({ type: "image.tweak", asset: asset.id })} />}>
                Edit
              </Menu.Item>
              <Menu.Item leftSection={<Copy size={14} aria-hidden />} renderRoot={(root) => <Link {...root} {...studio({ advanced: asset.id })} />}>
                Clone
              </Menu.Item>
              {layerize ? (
                <Menu.Item leftSection={<Layers3 size={14} aria-hidden />} renderRoot={(root) => <Link {...root} {...studio({ layerize: asset.id })} />}>
                  Layerize
                </Menu.Item>
              ) : null}
              <Menu.Item
                leftSection={<WandSparkles size={14} aria-hidden />}
                renderRoot={(root) => <Link {...root} {...studio({ medium: "video", type: "video.image", asset: asset.id })} />}
              >
                Animate
              </Menu.Item>
            </>
          ) : null}
          {asset.kind === "video" && asset.urls.original !== null ? (
            <>
              <Menu.Item
                leftSection={<SquarePlay size={14} aria-hidden />}
                renderRoot={(root) => (
                  <Link
                    {...root}
                    to="/teams/$teamId/brands/$brandId/editor/asset/$assetId"
                    params={{ teamId, brandId, assetId: asset.id }}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                )}
              >
                Open in the editor
              </Menu.Item>
              <Menu.Item
                leftSection={<Pencil size={14} aria-hidden />}
                renderRoot={(root) => (
                  <Link {...root} {...studio({ medium: "video", type: "video.edit", asset: asset.id })} />
                )}
              >
                Edit
              </Menu.Item>
            </>
          ) : null}
          {simple || textVideo || importedSimple ? (
            <Menu.Item
              closeMenuOnClick={false}
              disabled={promptText === ""}
              leftSection={prompt.state === "copied" ? <Check size={14} aria-hidden /> : <FilePlus size={14} aria-hidden />}
              onClick={() => prompt.copy(promptText)}
            >
              {prompt.state === "copied" ? "Copied" : prompt.state === "failed" ? "Failed to copy" : "Copy Prompt"}
            </Menu.Item>
          ) : null}
          {library && (simple || importedSimple) ? (
            // Live's toast, said by the item for its 5 s, as Copy link says "Link copied"; live's
            // 409 shows its words below, with the menu's other refusals (issue 908).
            <Menu.Item
              closeMenuOnClick={false}
              disabled={promptText === "" || saving.pending === asset.id}
              leftSection={saving.pending === asset.id ? <Loader size={14} /> : saving.said === asset.id ? <Check size={14} aria-hidden /> : <Type size={14} aria-hidden />}
              onClick={() => saving.save(asset.id)}
            >
              {saving.said === asset.id ? PROMPT_SAVED : "Save Prompt"}
            </Menu.Item>
          ) : null}
          {!library || folder === undefined ? null : (
            <Menu.Item
              leftSection={<FolderSymlink size={14} aria-hidden />}
              renderRoot={(root) =>
                projectLink === null ? (
                  <Link {...root} to="/teams/$teamId/brands/$brandId/library" params={{ teamId, brandId }} search={{ folder: folder.id }} />
                ) : (
                  projectLink(root, folder.id)
                )
              }
            >
              {folder.name}
            </Menu.Item>
          )}
          {opened && ((origin !== null && jobRead.isLoading) || (importedCreative && importedRead.isLoading)) ? (
            <Menu.Label>
              <Loader size={12} aria-label="Reading the file's generation" />
            </Menu.Label>
          ) : null}
        </Menu.Dropdown>
      </Menu>
      {downloadFailed ? (
        <Text size="xs" role="alert">
          Failed to download asset. Please try again.
        </Text>
      ) : null}
      {saving.error === null ? null : <ErrorAlert error={saving.error} />}
      {reindex.isError ? <ErrorAlert error={reindex.error} /> : null}
      <MoveToProject
        teamId={teamId}
        brandId={brandId}
        opened={filing}
        assetIds={[asset.id]}
        current={asset.folder_id}
        onClose={() => setFiling(false)}
      />
      <ConfirmModal
        opened={moving}
        title="Move asset"
        confirmLabel="Move"
        onConfirm={() => move.mutate({ teamId, brandId, data: { asset_id: asset.id } })}
        onCancel={() => {
          move.reset();
          setMoving(false);
        }}
        pending={move.isPending}
        error={move.isError ? <ErrorAlert error={move.error} /> : null}
      >
        <Text size="sm">
          Are you sure about to move this image to the &apos;AI Actors&apos; section. Once confirmed, this change cannot be reverted
        </Text>
      </ConfirmModal>
    </>
  );
}
